/**
 * showcaseCandidates — your best entry for every kind of PokéStop Showcase
 * (2026-09-19 owner ask: a Showcases page shaped like the Rocket page).
 *
 * Every showcase the game has ever run is BIGGEST WINS, judged by the score
 * in showcaseScore.ts (height, weight, IVs, and the flat XXL bonus). Since
 * Showcase Tuesdays (2026) most showcases are type-based (every Water-type
 * competes together), with species showcases on Community Days and buddy
 * showcases beside them. Because the score is normalised per species, a
 * Water showcase is simply won by the highest score among your Water
 * Pokémon, whatever the species, so this module groups your scanned
 * Pokémon by type and ranks each group by score.
 *
 * A record needs a height read to score at all; the ones without one are
 * counted per type so the page can say what a scan would unlock.
 *
 * Pure. Types come from the species table through myPokemonTypes, never a
 * scan (2026-09-20 owner direction).
 */
import type { MyPokemonRecord } from "./myPokemonStore";
import { showcaseScoreFor, type ShowcaseScore } from "./showcaseScore";
import type { PokeType } from "./types";
import { myPokemonTypes } from "./myPokemonTypes";

export const SHOWCASE_TYPE_ORDER: readonly PokeType[] = [
  "normal", "fire", "water", "grass", "electric", "ice", "fighting", "poison", "ground",
  "flying", "psychic", "bug", "rock", "ghost", "dragon", "dark", "steel", "fairy",
];

/** Rows shown per type before "show more". */
export const SHOWCASE_ROWS_PER_TYPE = 3;

export interface ShowcaseCandidate {
  record: MyPokemonRecord;
  score: ShowcaseScore;
}

export interface ShowcaseTypeGroup {
  type: PokeType;
  /** Scored, best first. */
  candidates: ShowcaseCandidate[];
  /** Records of this type with no height read (a page scan would score them). */
  unsized: number;
}

/** The trusted typing (species table; a regional form's own full read). */
function typesOf(r: MyPokemonRecord): PokeType[] {
  return myPokemonTypes(r);
}

/** Best first; ties break on the higher low bound, then the record id (stable). */
export function compareCandidates(a: ShowcaseCandidate, b: ShowcaseCandidate): number {
  return (
    b.score.score - a.score.score ||
    b.score.scoreLow - a.score.scoreLow ||
    a.record.id.localeCompare(b.record.id)
  );
}

/**
 * Every type in the canonical order, each with its ranked candidates. Types
 * with neither a scored nor an unsized record are left out.
 */
export function showcaseTypeGroups(records: readonly MyPokemonRecord[]): ShowcaseTypeGroup[] {
  const groups = new Map<PokeType, ShowcaseTypeGroup>();
  const groupFor = (t: PokeType): ShowcaseTypeGroup => {
    let g = groups.get(t);
    if (!g) {
      g = { type: t, candidates: [], unsized: 0 };
      groups.set(t, g);
    }
    return g;
  };
  for (const record of records) {
    const types = typesOf(record);
    if (types.length === 0) continue;
    const score = showcaseScoreFor(record);
    for (const t of types) {
      const g = groupFor(t);
      if (score) g.candidates.push({ record, score });
      else g.unsized += 1;
    }
  }
  for (const g of groups.values()) g.candidates.sort(compareCandidates);
  return SHOWCASE_TYPE_ORDER.filter((t) => groups.has(t)).map((t) => groups.get(t)!);
}

/** A buddy at any level (Good, Great, Ultra or Best) can enter the buddy showcases. */
export function isShowcaseBuddy(record: Pick<MyPokemonRecord, "isBestBuddy" | "buddyLevel">): boolean {
  return !!record.isBestBuddy || record.buddyLevel != null;
}

/**
 * The buddy showcases ("Good Buddy or higher", "Great Buddy or higher"),
 * judged biggest-wins like every other. The record page marks a buddy's
 * level (Good, Great, Ultra, Best: 2026-09-20), and Good already clears the
 * lower bar, so this is every marked buddy ranked by score, with the ones
 * lacking a size read counted.
 */
export function showcaseBuddyGroup(records: readonly MyPokemonRecord[]): { candidates: ShowcaseCandidate[]; unsized: number } {
  const candidates: ShowcaseCandidate[] = [];
  let unsized = 0;
  for (const record of records) {
    if (!isShowcaseBuddy(record)) continue;
    const score = showcaseScoreFor(record);
    if (score) candidates.push({ record, score });
    else unsized += 1;
  }
  candidates.sort(compareCandidates);
  return { candidates, unsized };
}

/** How many buddy suggestions the page makes. */
export const SHOWCASE_BUDDY_SUGGESTIONS = 3;

/**
 * Which Pokémon to walk next (2026-09-19 owner ask): the trainer's biggest
 * scorers that are NOT buddies yet, one per species, best first.
 * Buddying one of these puts the box's top score into the buddy showcases;
 * a Good Buddy is enough to enter, so the walk is short.
 */
export function showcaseBuddySuggestions(
  records: readonly MyPokemonRecord[],
  limit: number = SHOWCASE_BUDDY_SUGGESTIONS,
): ShowcaseCandidate[] {
  const scored: ShowcaseCandidate[] = [];
  for (const record of records) {
    if (isShowcaseBuddy(record)) continue;
    const score = showcaseScoreFor(record);
    if (score) scored.push({ record, score });
  }
  scored.sort(compareCandidates);
  const out: ShowcaseCandidate[] = [];
  const species = new Set<number>();
  for (const c of scored) {
    const id = c.record.speciesId ?? -1;
    if (species.has(id)) continue;
    species.add(id);
    out.push(c);
    if (out.length >= limit) break;
  }
  return out;
}

/** Your single best entry across every type, or null with nothing sized. */
export function showcaseOverallBest(groups: readonly ShowcaseTypeGroup[]): ShowcaseCandidate | null {
  let best: ShowcaseCandidate | null = null;
  for (const g of groups) {
    const top = g.candidates[0];
    if (top && (!best || compareCandidates(top, best) < 0)) best = top;
  }
  return best;
}

/**
 * What a score means at a showcase, in one word, from the community's
 * experience of Showcase Tuesdays: 1,000+ wins most, 850+ places, under
 * that is a participation reward.
 */
export type ShowcaseTier = "winner" | "contender" | "entry";
export function showcaseTier(score: ShowcaseScore): ShowcaseTier {
  if (score.scoreLow >= 1000) return "winner";
  if (score.scoreHigh >= 850) return "contender";
  return "entry";
}
